import { useEffect, useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  BulkApproveExpensablesDocument,
  ExpensablesDataCollectionDocument,
  ExpensesEntitiesExpensableResourceTypeEnum,
  GetExpensesCountDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
  UpdateAndApproveExpensableDocument,
  UpdateAndApproveExpensableMutationVariables,
} from 'generated/resources/graphql'
import { compact, pick } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import { getNewFileUploads } from 'modules/expenses/components/expenses/Form/Buttons/helpers'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import {
  isExpenseFormValues,
  isMileageFormValues,
  isPerDiemFormValues,
  validatePaymentOption,
} from 'modules/expenses/components/expenses/Form/Buttons/helpers/typechecks'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import { normalizeEffectiveOn, normalizeTaxes } from 'modules/expenses/hooks/useExpensable'
import { CustomizationField, Tax } from 'modules/expenses/hooks/useExpensesCustomization'
import {
  SignableOrganizationPerCountry,
  getSignedForOrganization,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { Expensable } from 'modules/expenses/types/Expensable'

interface ApproveExpensableButtonProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  expensable: Expensable
  canOverrideApprovalFlow: boolean
  customizationFields: CustomizationField[]
  canUpdateAndApprove?: boolean
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  formValid?: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  formDirty?: boolean
  trigger: () => Promise<boolean>
  onClose: () => void
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
}

const useBulkApprove = () => {
  const [approveExpensables, { loading }] = useMutation(BulkApproveExpensablesDocument, {
    extractorFn: (data) => data.expensesMutations.bulkApproveExpensesExpensable,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      GetExpensesCountDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    approve: (ids: string[], overrideApprovalFlow?: boolean) =>
      approveExpensables({ variables: { ids, overrideApprovalFlow } }),
  }
}

const useUpdateAndApprove = () => {
  const [updateAndApproveExpensable, { loading }] = useMutation(
    UpdateAndApproveExpensableDocument,
    {
      extractorFn: (data) => data.expensesMutations.updateAndApproveExpensesExpensable,
      refetchQueries: [
        ExpensablesDataTableDocument,
        ExpensablesDataCollectionDocument,
        GetGroupsDocument,
        MyGroupedExpensablesDataTableDocument,
        MyGroupedExpensablesDataCollectionDocument,
        GetExpensesCountDocument,
      ],
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    updateAndApprove: (variables: UpdateAndApproveExpensableMutationVariables) =>
      updateAndApproveExpensable({ variables }),
    loading,
  }
}

const UpdateExpenseFields = [
  'employeeId',
  'amount',
  'currency',
  'category',
  'subcategory',
  'documentNumber',
  'description',
  'reference',
  'merchantName',
  'merchantTin',
  'paymentMethod',
  'budgetId',
  'payment',
  'reimbursableCurrency',
  'exchangeRate',
]
const UpdateMileageFields = [
  'budgetId',
  'categoryId',
  'currency',
  'description',
  'employeeId',
  'files',
  'from',
  'internalReference',
  'projects',
  'rate',
  'subcategory',
  'to',
  'units',
]

interface Project {
  projectWorker?: { id: string } | null
  subproject?: { id?: string | null } | null
}

const formatProjects = (projects: Project[]) => {
  return projects
    .filter((project) => project.projectWorker?.id)
    .map((project) => ({
      projectWorkerId: project.projectWorker?.id ?? '0',
      subprojectId: project.subproject?.id || undefined,
    }))
}

const formatPerDiemRatesForPayload = (rates: PerDiemFormValues['perDiemRates']) => {
  if (!rates) return []

  // Filter out rates with null or undefined rateConfigurationId
  // This is because the backend expects a rateConfigurationId defined, but we start with an empty rate as a placeholder
  // The user can still submit with the empty rate to save as draft, but we need to filter it out before sending to the backend
  return rates
    .filter(
      (rate): rate is NonNullable<typeof rate> & { rateConfigurationId: string } =>
        rate.rateConfigurationId !== null && rate.rateConfigurationId !== undefined
    )
    .map((rate) => ({
      id: rate.id,
      rateConfigurationId: rate.rateConfigurationId ?? '0',
      totalAmountCents: rate.totalAmount,
      totalDays: rate.units,
    }))
}

const ApproveExpensableButton = ({
  formType,
  expensable,
  canOverrideApprovalFlow,
  canUpdateAndApprove,
  customizationFields,
  trigger,
  formValues,
  formDirty,
  formValid,
  taxAgencyOrganization,
  onClose,
  onLoadingChange,
  disabled,
}: ApproveExpensableButtonProps) => {
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const canReviewStep = expensable.currentUserApprovalStatus === 'pending'

  const globalApproveLabel = i18n.t('expenses_v2.buttons.global_approve')
  const canGlobalApprove = canOverrideApprovalFlow

  const { updateAndApprove, loading: loadingUpdateAndApprove } = useUpdateAndApprove()
  const { approve, loading: loadingBulkApprove } = useBulkApprove()
  const loadingApprove = loadingBulkApprove || loadingUpdateAndApprove || !formValid

  useEffect(() => {
    onLoadingChange?.(loadingApprove)
  }, [loadingApprove, onLoadingChange])

  const { company } = useCurrentSession()
  const isPending = expensable.status === 'pending'

  // Strategy pattern for building submit data
  const submitStrategies = {
    expense: (
      values: ExpenseFormValues | PerDiemFormValues
    ): UpdateAndApproveExpensableMutationVariables => {
      if (!('taxes' in values)) {
        throw new Error('Expense strategy requires ExpenseFormValues')
      }

      const formData = pick(values, [...UpdateExpenseFields])
      const filesData = getNewFileUploads(values.files)

      const taxes =
        values.taxes.map((tax: Tax) => {
          return {
            amount: toCents(tax.amount ?? 0),
            base_amount: toCents(tax.base_amount ?? 0),
            percentage: tax.percentage ?? 0,
          }
        }) ?? []

      const signedForOrganization = getSignedForOrganization({
        filesData: values.files || [],
        expensable,
        filesIdToRemove: values.filesIdToRemove || [],
        taxAgencyOrganization,
      })

      const isAutomatic = values.creationType === 'automatic'
      const merchantName = isAutomatic ? expensable?.expense?.merchantName : values.merchantName

      return {
        ...formData,
        id: expensable.id,
        documentType: values.documentType,
        amount: values.amount != null ? toCents(values.amount) : expensable.amount,
        companyId: company.id,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        merchantName: merchantName,
        userMerchant: values.merchantName,
        taxes: normalizeTaxes({
          taxes: taxes,
          vatAmount: taxes[0]?.amount ?? null,
          customizationFields,
        }),
        projects: values.projects.map((project) => ({
          projectWorkerId: project.projectWorker?.id ?? '0',
          subprojectId: project.subproject?.id,
        })),
        budgetId: values.budgetId,
        reimbursableAmount: values.reimbursableAmount ? toCents(values.reimbursableAmount) : null,
        reimbursableCurrency: values.reimbursableCurrency,
        files: filesData,
        filesIdToRemove: values.filesIdToRemove,
        signedForOrganization: signedForOrganization,
        payment: validatePaymentOption(values.payment),
        internalReference: values.internalReference,
      }
    },

    perdiem: (values: PerDiemFormValues): UpdateAndApproveExpensableMutationVariables => {
      if (!('perDiemRates' in values)) {
        throw new Error('PerDiem strategy requires PerDiemFormValues')
      }

      const calculatedAmount =
        values.perDiemRates?.reduce((acc, rate) => acc + (rate.totalAmount || 0), 0) || 0

      const formattedRates = formatPerDiemRatesForPayload(values.perDiemRates || [])
      const filesData = getNewFileUploads(values.files)
      const employeeId = expensable.owner?.id

      return {
        id: expensable.id,
        amount: calculatedAmount,
        budgetId: values.budgetId,
        companyId: company.id,
        currency: values.currency,
        description: values.description,
        effectiveOn: normalizeEffectiveOn(values.start_date),
        files: filesData,
        filesIdToRemove: values.filesIdToRemove || [],
        from: values.from,
        projects: formatProjects(values.projects),
        subcategory: values.subcategory,
        taxes: [],
        to: values.to,
        perDiem: {
          amount: calculatedAmount,
          budgetId: values.budgetId,
          companyId: company.id,
          currency: values.currency,
          deletedRates: [],
          description: values.description,
          employeeId: employeeId,
          endDate: values.end_date,
          files: filesData,
          filesIdToRemove: values.filesIdToRemove || [],
          from: values.from,
          internalReference: values.internalReference,
          projects: formatProjects(values.projects),
          rates: formattedRates,
          startDate: values.start_date,
          subcategory: values.subcategory,
          to: values.to,
        },
      }
    },
    // For some reason the mileage  variables are not present on the mutation
    // Yet the data is still submitted
    mileage: (values: MileageFormValues) => {
      if (!('mileage' in values)) {
        throw new Error('Mileage strategy requires MileageFormValues')
      }

      const formData = pick(values, [...UpdateMileageFields])
      const originLatitude = values.originLatitude ? parseFloat(values.originLatitude) : null
      const originLongitude = values.originLongitude ? parseFloat(values.originLongitude) : null
      const destinationLatitude = values.destinationLatitude
        ? parseFloat(values.destinationLatitude)
        : null
      const destinationLongitude = values.destinationLongitude
        ? parseFloat(values.destinationLongitude)
        : null
      const roundTrip = values.roundTrip ?? false
      const amount = toCents(values.amount)
      const calculatedMileage = toCents(values.calculatedMileage)
      const mileage = toCents(values.mileage)
      const filesData = getNewFileUploads(values.files)
      const effectiveOn = normalizeEffectiveOn(values.effectiveOn)

      return {
        ...formData,
        id: expensable.id,
        amount: amount,
        mileage: mileage,
        companyId: company.id,
        calculatedMileage: calculatedMileage,
        originLatitude: originLatitude,
        originLongitude: originLongitude,
        destinationLatitude: destinationLatitude,
        destinationLongitude: destinationLongitude,
        roundTrip: roundTrip,
        projects: formatProjects(values.projects),
        files: filesData,
        filesIdToRemove: values.filesIdToRemove,
        from: values.from,
        internalReference: values.internalReference,
        rate: values.rate?.toString(),
        effectiveOn: effectiveOn,
        subcategory: values.subcategory,
        to: values.to,
        units: values.units,
      }
    },
  }

  const buildSubmitData = (): UpdateAndApproveExpensableMutationVariables => {
    if (formType === 'expense' && isExpenseFormValues(formValues, formType)) {
      return submitStrategies.expense(formValues)
    } else if (formType === 'perdiem' && isPerDiemFormValues(formValues, formType)) {
      return submitStrategies.perdiem(formValues)
    } else if (formType === 'mileage' && isMileageFormValues(formValues, formType)) {
      return submitStrategies.mileage(formValues)
    }

    throw new Error(`No strategy found for form type: ${formType}`)
  }

  const triggerValidation = async () => {
    const isValid = await trigger()
    return isValid
  }

  const handleUpdateAndApprove = async (globalApprove: boolean) => {
    const canBeSubmitted = await triggerValidation()

    if (!canBeSubmitted && isPending) return
    const data = buildSubmitData()
    const result = await updateAndApprove({
      ...data,
      overrideApprovalFlow: globalApprove,
    })

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })
    if (!handled.success) return handled
    const approvedExpensable = handled.data?.expensable
    if (!approvedExpensable) return handled
    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.success_messages.all_items'),
    })
    onClose()
  }

  const handleApprove = async (globalApprove: boolean) => {
    const canBeSubmitted = await triggerValidation()
    if (!canBeSubmitted && isPending) return

    const result = await approve([expensable.id], globalApprove)
    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })
    if (!handled.success) return handled
    const approvedExpensable = handled.data?.expensables ? handled.data.expensables[0] : null
    if (!approvedExpensable) return handled
    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.success_messages.all_items'),
    })
    onClose()
  }

  const handleGlobalApprove = () => {
    setReviewModalOpen(true)
  }
  const submit = async () => {
    if (formDirty) {
      await handleUpdateAndApprove(true)
    } else {
      await handleApprove(true)
    }
  }

  const approveOptions = compact([
    canReviewStep && {
      title: i18n.t('expenses_v2.buttons.step_approve'),
      mainTitle: i18n.t('expenses_v2.buttons.approve'),
      onClick: canUpdateAndApprove
        ? () => handleUpdateAndApprove(false)
        : () => handleApprove(false),
    },
    canGlobalApprove && {
      title: globalApproveLabel,
      mainTitle: globalApproveLabel,
      onClick: handleGlobalApprove,
    },
  ])

  if (approveOptions.length === 0) return null
  const mainApproveOption = approveOptions[0]

  if (!mainApproveOption) return null

  if (approveOptions.length === 1) {
    return (
      <>
        {reviewModalOpen && (
          <ReviewModal
            type="approve"
            title={i18n.t('expenses_v2.list.actions.modal.title_global_approve')}
            description={i18n.t('expenses_v2.list.actions.modal.description_global_approve')}
            onSubmit={submit}
            onClose={() => setReviewModalOpen(false)}
          />
        )}
        <Button
          type="positive"
          disabled={loadingApprove || disabled}
          onClick={mainApproveOption.onClick}
        >
          {mainApproveOption.mainTitle}
        </Button>
      </>
    )
  }

  return (
    <>
      {reviewModalOpen && (
        <ReviewModal
          type="approve"
          title={i18n.t('expenses_v2.list.actions.modal.title_global_approve')}
          description={i18n.t('expenses_v2.list.actions.modal.description_global_approve')}
          onSubmit={submit}
          onClose={() => setReviewModalOpen(false)}
        />
      )}
      <Button.Split
        type="positive"
        label={mainApproveOption.mainTitle}
        disabled={loadingApprove || disabled}
        onClick={mainApproveOption.onClick}
        loading={loadingApprove}
        iconName={loadingApprove ? 'upload' : undefined}
      >
        {approveOptions.map((approveOption, i) => (
          <Button.Split.Option
            key={i}
            disabled={loadingApprove || disabled}
            onClick={approveOption.onClick}
          >
            {approveOption.title}
          </Button.Split.Option>
        ))}
      </Button.Split>
    </>
  )
}

export default ApproveExpensableButton
